/**
 * Multi-Day Calendar View Helper Functions
 */

import dayjs from 'dayjs';
import type { CalendarEvent } from '@/api/types/calendars.types';

/**
 * Calculate visible days based on current date, offset and count
 * @param currentDate - Base date (usually today)
 * @param dayOffset - Number of days to offset from current date (negative = past)
 * @param dayCount - Number of consecutive days to display
 * @param timezone - User's timezone
 * @returns Array of Dayjs objects representing visible days
 */
export const getVisibleDays = (
  currentDate: Date,
  dayOffset: number,
  dayCount: number,
  timezone: string
): dayjs.Dayjs[] => {
  const baseDate = dayjs.tz(currentDate, timezone).startOf('day');
  const startDate = baseDate.add(dayOffset, 'day');

  return Array.from({ length: dayCount }, (_, i) => startDate.add(i, 'day'));
};

/**
 * Check if a given day is today
 * @param day - Day to check
 * @param timezone - User's timezone
 * @returns True if day is today
 */
export const isToday = (day: dayjs.Dayjs, timezone: string): boolean => {
  const today = dayjs().tz(timezone).startOf('day');
  return day.startOf('day').isSame(today);
};

/**
 * Calculate offset to Monday of current week
 * @param date - Current date
 * @returns Negative number representing days back to Monday
 */
export const calculateMondayOffset = (date: Date): number => {
  const dayOfWeek = dayjs(date).day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Convert to Monday-based (0 = Monday, 6 = Sunday)
  const mondayBased = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return -mondayBased;
};

/**
 * Filter events that occur on a specific day
 * @param events - All events
 * @param day - Day to filter for
 * @param timezone - User's timezone
 * @returns Events that overlap with the specified day
 */
export const getEventsForDay = (
  events: CalendarEvent[],
  day: dayjs.Dayjs,
  timezone: string
): CalendarEvent[] => {
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');

  return events.filter((event) => {
    const eventStart = dayjs.utc(event.start_time).tz(timezone);
    const eventEnd = dayjs.utc(event.end_time).tz(timezone);

    // Event overlaps with this day if:
    // - Event starts during this day, OR
    // - Event ends during this day, OR
    // - Event spans the entire day
    return (
      ((eventStart.isAfter(dayStart) || eventStart.isSame(dayStart)) &&
        eventStart.isBefore(dayEnd)) ||
      (eventEnd.isAfter(dayStart) &&
        (eventEnd.isBefore(dayEnd) || eventEnd.isSame(dayEnd))) ||
      (eventStart.isBefore(dayStart) && eventEnd.isAfter(dayEnd))
    );
  });
};

/**
 * Get day index for today (0-based)
 * @param visibleDays - Array of visible days
 * @param timezone - User's timezone
 * @returns Index of today, or -1 if today is not visible
 */
export const getTodayIndex = (
  visibleDays: dayjs.Dayjs[],
  timezone: string
): number => {
  return visibleDays.findIndex((day) => isToday(day, timezone));
};
